import React, { useState } from "react";
import { toast } from "sonner";
import { addMonths, addWeeks, addDays, startOfMonth, endOfMonth, startOfWeek, endOfWeek, startOfDay, endOfDay, format, setHours } from "date-fns";
import { ru } from "date-fns/locale";
import { useFetch } from "../../lib/useFetch";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import FilterPills from "./FilterPills";
import EventDialog from "./EventDialog";
import { MonthView, WeekView, DayView } from "./CalendarViews";

const VIEWS = [["month", "Месяц"], ["week", "Неделя"], ["day", "День"]];
const SCOPES = [["mine", "Мои"], ["deadlines", "Дедлайны"], ["all", "Все"]];
const SHIFT = { month: addMonths, week: addWeeks, day: addDays };
const WEEK = { weekStartsOn: 1 };

function rangeFor(view, date) {
  if (view === "month") return [startOfWeek(startOfMonth(date), WEEK), endOfWeek(endOfMonth(date), WEEK)];
  if (view === "week") return [startOfWeek(date, WEEK), endOfWeek(date, WEEK)];
  return [startOfDay(date), endOfDay(date)];
}

function titleFor(view, date, from, to) {
  if (view === "day") return format(date, "d MMMM yyyy, EEEE", { locale: ru });
  if (view === "week") return `${format(from, "d MMM", { locale: ru })} – ${format(to, "d MMM yyyy", { locale: ru })}`;
  return format(date, "LLLL yyyy", { locale: ru });
}

export default function CalendarPanel() {
  const [view, setView] = useState("month");
  const [scope, setScope] = useState("all");
  const [date, setDate] = useState(() => new Date());
  const [editing, setEditing] = useState(null);
  const [from, to] = rangeFor(view, date);
  const [data, reload] = useFetch(`/admin/calendar?start=${encodeURIComponent(from.toISOString())}&end=${encodeURIComponent(to.toISOString())}&scope=${scope}`);
  const items = data?.items || [];

  const openItem = (it) => (it.kind === "personal" ? setEditing(it) : toast(`🟠 Дедлайн: ${it.title}`, { description: "Изменить дедлайн можно в разделе «Заказы»" }));
  const openDay = (d) => { setDate(d); setView("day"); };
  const nav = "btn-outline !py-1.5 !px-3";

  return (
    <div className="glass rounded-2xl p-6" data-testid="panel-calendar">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <button className={nav} onClick={() => setDate(SHIFT[view](date, -1))} data-testid="calendar-prev"><ChevronLeft size={14} /></button>
          <button className={`${nav} text-xs`} onClick={() => setDate(new Date())} data-testid="calendar-today">Сегодня</button>
          <button className={nav} onClick={() => setDate(SHIFT[view](date, 1))} data-testid="calendar-next"><ChevronRight size={14} /></button>
          <h3 className="font-heading text-xl font-bold capitalize ml-2" data-testid="calendar-title">{titleFor(view, date, from, to)}</h3>
        </div>
        <button onClick={() => setEditing({ start: setHours(startOfDay(date), 10).toISOString() })} className="btn-gold !py-2 inline-flex items-center gap-2" data-testid="calendar-add-btn">
          <Plus size={14} /> Событие
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-4 mb-5">
        <FilterPills options={VIEWS} value={view} onChange={setView} testid="calendar-view" />
        <FilterPills options={SCOPES} value={scope} onChange={setScope} testid="calendar-scope" />
        <div className="text-xs text-gray-400 flex gap-3 ml-auto"><span>🟡 личные</span><span>🟠 дедлайны</span><span>🔴 просроченные</span></div>
      </div>
      {view === "month" && <MonthView from={from} date={date} items={items} onDay={openDay} onItem={openItem} />}
      {view === "week" && <WeekView from={from} items={items} onDay={openDay} onItem={openItem} />}
      {view === "day" && <DayView date={date} items={items} onItem={openItem} />}
      {editing && <EventDialog event={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
    </div>
  );
}
